
import React, { lazy, Suspense, useEffect } from 'react';
import './index.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import OverseasRedirectModal from './components/OverseasRedirectModal';

// 各页面 title 配置
const PAGE_TITLES = {
    '/': 'AI视频去字幕在线工具 - 一键消除硬字幕水印，短剧二创必备 | 550W AI',
    '/purchase': '套餐购买 - 550W AI去字幕',
    '/team': '团队版 - 550W AI去字幕 | 多成员协作批量处理',
    '/api-open': 'API 开放能力 - 550W AI去字幕 | 开发者接入文档',
    '/openclaw': 'OpenClaw 智能体 - 550W AI去字幕 | 对话式去字幕',
};

// 路由切换时上报百度统计 PV + 设置页面标题
function RouteTracker() {
    const location = useLocation();
    useEffect(() => {
        const path = location.pathname;
        // 设置页面标题
        document.title = PAGE_TITLES[path] || PAGE_TITLES['/'];
        // 百度统计 PV 上报
        if (window._hmt) {
            window._hmt.push(['_trackPageview', path]);
        }
    }, [location.pathname]);
    return null;
}

// 路由级懒加载 + chunk 失败自动重试
const lazyWithRetry = (importFn) => lazy(() =>
    importFn().catch(() =>
        // 第一次失败重试一次
        importFn().catch((error) => {
            // 重试也失败，自动刷新页面加载新版本
            const lastReload = sessionStorage.getItem('chunk_reload');
            if (!lastReload || Date.now() - Number(lastReload) > 10000) {
                sessionStorage.setItem('chunk_reload', String(Date.now()));
                window.location.reload();
            }
            throw error;
        })
    )
);

const Index = lazyWithRetry(() => import('./index/index'));
const Purchase = lazyWithRetry(() => import(/* webpackPrefetch: true */ './purchase/index'));
const TeamPage = lazyWithRetry(() => import('./team/index'));
const ApiOpenPage = lazyWithRetry(() => import('./api-open/index'));
const OpenClawPage = lazyWithRetry(() => import('./openclaw/index'));

// chunk 加载失败时自动刷新，避免白屏
class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }
    componentDidCatch(error) {
        // webpack chunk 加载失败（网络中断、部署更新等）
        if (error && (error.name === 'ChunkLoadError' || /Loading chunk/.test(error.message))) {
            // 避免无限刷新：5秒内只刷新一次
            const lastReload = sessionStorage.getItem('chunk_reload');
            if (!lastReload || Date.now() - Number(lastReload) > 5000) {
                sessionStorage.setItem('chunk_reload', String(Date.now()));
                window.location.reload();
            }
        }
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{background:'#0a0a0f',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#a1a1aa',flexDirection:'column',gap:'16px'}}>
                    <div>页面加载出错</div>
                    <button onClick={() => window.location.reload()} style={{padding:'8px 24px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.12)',background:'#6366f1',color:'#fff',cursor:'pointer'}}>
                        点击刷新
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function LoadingScreen() {
    return (
        <div style={{
            background: '#0a0a0f',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '24px',
        }}>
            {/* 品牌光环动画 */}
            <div style={{ position: 'relative', width: 64, height: 64 }}>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: '3px solid rgba(99,102,241,0.15)',
                }} />
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: '3px solid transparent',
                    borderTopColor: '#6366f1',
                    animation: 'spin 0.8s linear infinite',
                }} />
                <div style={{
                    position: 'absolute',
                    inset: '8px',
                    borderRadius: '50%',
                    border: '3px solid transparent',
                    borderTopColor: '#818cf8',
                    animation: 'spin 1.2s linear infinite reverse',
                }} />
                <div style={{
                    position: 'absolute',
                    inset: '18px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                    opacity: 0.9,
                    animation: 'pulse 1.5s ease-in-out infinite',
                }} />
            </div>
            <div style={{
                color: '#a1a1aa',
                fontSize: '14px',
                letterSpacing: '2px',
                animation: 'fadeIn 0.6s ease',
            }}>
                加载中
            </div>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulse { 0%,100% { opacity: 0.6; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
}

function App() {
    return (
        <ChunkErrorBoundary>
            <BrowserRouter>
                <RouteTracker />
                <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/purchase" element={<Purchase />} />
                        <Route path="/team" element={<TeamPage />} />
                        <Route path="/api-open" element={<ApiOpenPage />} />
                        <Route path="/openclaw" element={<OpenClawPage />} />
                    </Routes>
                    <OverseasRedirectModal />
                </Suspense>
            </BrowserRouter>
        </ChunkErrorBoundary>
    );
}

export default App;
